import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
export function WhereAIFits() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-780" data-mp="35cnoyqjpswzr">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-781" data-mp="3acjrsmgoeidq">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-782" data-mp="3aafi1nw3ip5j">
          <BlueprintGrid data-id="element-783" data-mp="1vygv04hhf7z" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-784" data-mp="1uvosovfd6qh">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-785" data-mp="21spzn8ldvsgj">
            Where AI Fits
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-786" data-mp="1xos0wyzz0jo0">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-787" data-mp="26q8bp7up6nsr">
              AI is the answer.
            </span>{' '}
            <span className="text-navy" data-id="element-788" data-mp="2ghcs8c00ugpp">
              But not to the question most people are asking.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mb-8 space-y-4" data-id="element-789" data-mp="2ufkm2b1rh1wn">
            <p data-id="element-790" data-mp="2pfoigcwz7chm">AI can absolutely create leverage.</p>
            <p data-id="element-791" data-mp="103w2fvkl4b5x">
              It can improve speed, consistency, signal quality,
              troubleshooting, decision support, and bottom-line performance.
            </p>
            <p data-id="element-792" data-mp="2v69arqmaoych">
              But it does not fix fuzzy rules, weak handoffs, scattered
              knowledge, or workflows nobody can define cleanly.
            </p>
            <p data-id="element-793" data-mp="3076rdyrnpkbv">
              If the business logic is unclear, AI usually accelerates noise.
            </p>
            <p className="font-medium text-navy" data-id="element-794" data-mp="356jiwwth3fks">
              If the workflow is sound, AI can become a real lever.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col" data-id="element-795" data-mp="3e42bqndcyy78">
            <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 w-fit" data-id="element-796" data-mp="3j3euox13zl4j">
              <span data-id="element-797" data-mp="3ssyh2co15x45">Book an Honest Conversation</span>
              <ArrowRight className="w-5 h-5" data-id="element-798" data-mp="qh1d8ytnqjc" />
            </Link>
            <p className="text-sm text-text-muted mt-2" data-id="element-799" data-mp="o4au7f007uo">
              45 minutes. No pitch. Just clarity.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* AI works best when attached to something real */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-800" data-mp="1l7q8atrkeo1q">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-801" data-mp="1h64ospkizbik">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-802" data-mp="28x2gbqb5oq2g">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-803" data-mp="24vgwtm449dja">
              AI works best when it is attached to something real
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-804" data-mp="1v6z13jap1wmi">
            <p className="text-text" data-id="element-805" data-mp="1v4cqtbwf530l">
              Most AI efforts do not stall because the model is weak.
            </p>
            <p className="text-text" data-id="element-806" data-mp="2nubcqyfbgk11">
              They stall because the business is not clear enough yet.
            </p>
            <p className="text-text" data-id="element-807" data-mp="27eao92kom0dp">
              The logic is still trapped in people. Definitions drift. Ownership
              is fuzzy. Exceptions are doing the real work. The team cannot
              explain what good looks like clearly enough for the system to
              support it.
            </p>
            <p className="text-text" data-id="element-808" data-mp="23ea6b7ydj71w">
              That is why AI so often creates the appearance of progress without
              delivering operating leverage.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-809" data-mp="2v3v6fj90gtlx">
              Technology follows clarity. We do not automate confusion.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What AI-ready actually means */}
      <section id="ai-ready" className="relative py-24 px-6 bg-[#8BA89F26] grain border-y border-seaglass/20 overflow-hidden" data-id="element-810" data-mp="2ma9rnzwujcci">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-811" data-mp="3etyshx3t021n" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-812" data-mp="3o7phexvyhrht">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-813" data-mp="1rmjn1q13l5c">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-814" data-mp="3sy5v8j3ovf4p">AI-ready</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-815" data-mp="3t0s3ym2bg0oi">
              What AI-ready actually means
            </h2>
            <div className="text-lg text-text space-y-4" data-id="element-816" data-mp="318uc55w4cpe8">
              <p data-id="element-817" data-mp="3f7kaspyl6x4p">
                AI-ready does not mean the company is modern, technical, or full
                of tools.
              </p>
              <p data-id="element-818" data-mp="3a6d46u86k3zm">
                It means the workflow is clear enough for AI to support
                something real.
              </p>
              <p data-id="element-819" data-mp="phpc3ixuut4h">That usually requires:</p>
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="mb-10" data-id="element-820" data-mp="pk357m8z5v9e">
            <ul className="space-y-3 pl-1" data-id="element-821" data-mp="xhmxk8ta575v">
              {['clear definitions', 'clear decision rules', 'clear ownership', 'usable knowledge outside people\u2019s heads', 'clear guardrails around where AI helps and where humans still decide'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-822" data-mp="dupbi58nml0n" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-823" data-mp="dscld4sqt4ci" />
                  <span className="text-body text-text" data-id="element-824" data-mp="irfq0o4t98jy">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-825" data-mp="sjtqldk5k94j">
            <div className="border-l-2 border-seaglass pl-8 py-2 space-y-2" data-id="element-826" data-mp="skmsa04988ft">
              <p className="text-body text-text font-medium" data-id="element-827" data-mp="3r8iw13rxijw1">
                If those are missing, the answer is usually not more AI.
              </p>
              <p className="text-body text-text font-medium" data-id="element-828" data-mp="3oovxxefoxrd5">
                It is better structure first.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* How I use AI in this work */}
      <section className="py-24 px-6 bg-paper" data-id="element-829" data-mp="3epop94jwikwd">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-830" data-mp="3esj84rn9nxno">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-831" data-mp="30ya2cmmvhzk1">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-832" data-mp="2w08b3efamtxh">
              How I use AI in this work
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-833" data-mp="2qzarqyijrtue">
            <p className="text-body text-text" data-id="element-834" data-mp="2m0r6z1fowo3m">
              I use AI to help find leverage and improve bottom-line
              performance.
            </p>

            <p className="text-body text-text" data-id="element-835" data-mp="9s0ts0590nl7">That can include:</p>

            <ul className="space-y-3" data-id="element-836" data-mp="9poejuqvkrbu">
              {['clarifying decision rules before automation', 'tightening handoffs so outputs do not die in the middle', 'cleaning up definitions so systems produce usable signals', 'redesigning exception paths that currently depend on judgment trapped in people', 'identifying where AI can support speed, consistency, routing, drafting, summarizing, troubleshooting, or signal detection without increasing confusion'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-837" data-mp="3da143lya151z" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-838" data-mp="3i6rkkadzu2nx" />
                  <span className="text-body text-text" data-id="element-839" data-mp="347jh35ass8dx">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-navy font-medium" data-id="element-840" data-mp="3883em9za9e0l">
              The point is not to force AI into the business.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-841" data-mp="2pmxfsaruqunc">
              The point is to make sure it is accelerating something real.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* A simple example */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-842" data-mp="2tmem16f3rvtk">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-843" data-mp="2tp0wbdtdopfh">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-844" data-mp="3hb7bql54t7fq">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-845" data-mp="3danezioh05u4">
              A simple example
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-846" data-mp="3rapx960ox19w">
            <p className="text-body text-text" data-id="element-847" data-mp="3mcelgpqu4gp1">
              A founder had his team build a service ticket platform to improve
              the customer experience.
            </p>

            <p className="text-body text-text" data-id="element-848" data-mp="1ff513991vcd0">
              The interface was fine. But the real leverage was not the UI.
            </p>

            <p className="text-body text-navy font-semibold" data-id="element-849" data-mp="1beba270b9hfw">
              It was the clearer workflow truth underneath it.
            </p>

            <p className="text-body text-text" data-id="element-850" data-mp="234zathy2okt1">
              Once service data and account data were lined up properly, the
              business could finally see which requests came back inside 30 days
              of onboarding, where installation quality was breaking down
              upstream, and which industries were consuming the most service
              time relative to the revenue they produced.
            </p>

            <p className="text-body text-text" data-id="element-851" data-mp="iqfdr3enlrjn">
              That created signals the business could actually trust.
            </p>

            <div className="py-8 my-4 border-y hairline-top hairline-bottom space-y-4" data-id="element-852" data-mp="dsdx0p4pa4bc">
              <p className="text-body text-text" data-id="element-853" data-mp="3o7bfvjatgj1">
                Only after that did AI become useful in the next layer.
              </p>
              <p className="text-body text-text" data-id="element-854" data-mp="dktuqfvz7ziy">
                With cleaner workflow truth in place, we built a custom GPT for
                field service agents using equipment manuals as reference
                material so they could troubleshoot faster from their phones.
              </p>
            </div>

            <p className="text-body text-navy font-semibold" data-id="element-855" data-mp="rb5oskc4g8cq">
              That is where AI actually earns its role.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What not to do */}
      <section className="py-24 px-6 bg-paper" data-id="element-856" data-mp="118tl3epdlnx0">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-857" data-mp="2y2el895lof7g">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-858" data-mp="2v1l5fzxw5grn">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-859" data-mp="2q1yu5w1mialh">
              What not to do
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-860" data-mp="34wwp8wo5pe69">
            <ul className="space-y-3" data-id="element-861" data-mp="2zyblwodlyedl">
              {['Do not ask AI to fix a business that still runs on unclear rules.', 'Do not automate a workflow nobody can define cleanly.', 'Do not mistake tool activity for operating leverage.', 'Do not hand a model the job of interpreting what the business itself has not clarified yet.'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-862" data-mp="3isqnmlrb0l54" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-863" data-mp="lazekjufsq2" />
                  <span className="text-text" data-id="element-864" data-mp="3sq524fto2zus">{item}</span>
                </li>)}
            </ul>

            <p className="text-text font-medium text-navy" data-id="element-865" data-mp="f89oi8v8ab1m">
              Fix the recurring failure first.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-866" data-mp="66s9yuxgyhf1">
              Then decide what to automate.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* The point */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-867" data-mp="3smczew3gmj38">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-868" data-mp="3skthz262i1zv">
          <motion.div variants={fadeIn} className="border-l-2 border-seaglass pl-8 py-2" data-id="element-869" data-mp="3nn0cycgi3e16">
            <p className="eyebrow mb-3 text-seaglass" data-id="element-870" data-mp="39n8rxrrkgxdo">The point</p>
            <p className="text-body text-text mb-3" data-id="element-871" data-mp="3emlnr20f1ese">
              If AI is not helping yet, the problem may not be AI.
            </p>
            <p className="text-body text-text mb-3" data-id="element-872" data-mp="ktkc7k7y0reb">
              It may be that the workflow, ownership, definitions, or business
              logic are still too unclear to support it.
            </p>
            <p className="text-body text-navy font-medium mb-3" data-id="element-873" data-mp="c1a4qaofn00r">
              AI can be a real lever.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-874" data-mp="72p9kpo1f6j6">
              But only when it is attached to something the business can already
              trust.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-875" data-mp="3p8bv1mo09yvi">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-876" data-mp="3k9qufm4udd6j">
          <BlueprintGrid data-id="element-877" data-mp="3kble6x4dmz91" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-878" data-mp="1nh712jylom80">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-879" data-mp="1q13nlsg8jduh">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-880" data-mp="1uzorc1up4fno">
              Fix the recurring failure first.
            </span>{' '}
            <span className="text-white" data-id="element-881" data-mp="24r2lrv7sip9y">Then decide what to automate.</span>
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-882" data-mp="173kdsmlydr70">
            If AI is not helping yet, the problem may not be AI.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-883" data-mp="1c2x118gql0a4">
            <span data-id="element-884" data-mp="1l0h48my5t6nd">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-885" data-mp="2sfc4updmcyxe" />
          </Link>
          <p className="text-sm text-white/55 mt-4" data-id="element-886" data-mp="2wge6tdyx7p32">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}